import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styles from './CharacterCard.module.css';

const CharacterCard = ({ 
  character, 
  onDelete,
  onAbandon,
  editPath,
  viewPath,
  profilePath,
  themePath,
  onEditAvatar,
  showUniverse = true,
  showRoleRp = false,
  roleRp,
  roleRpPlaceholder = 'Non défini',
  onRoleRpClick,
  roleRpEditor = null,
  variant = 'grid',
}) => {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const handleView = () => {
    if (viewPath) {
      navigate(viewPath);
      setDropdownOpen(false);
    }
  };

  const handleEdit = () => {
    if (editPath) {
      navigate(editPath);
      setDropdownOpen(false);
    }
  };

  const handleEditAvatar = () => {
    if (onEditAvatar) {
      onEditAvatar(character);
      setDropdownOpen(false);
    }
  };

  const handleAbandon = () => {
    if (onAbandon && window.confirm(
      'Abandonner ce personnage ? Vous perdrez le contrôle du personnage, mais son historique RP sera conservé.'
    )) {
      onAbandon(character.id);
      setDropdownOpen(false);
    }
  };

  const handleDelete = () => {
    if (onDelete && window.confirm('Êtes-vous sûr de vouloir supprimer ce personnage ?')) {
      onDelete(character.id);
      setDropdownOpen(false);
    }
  };

  const getStatusMessage = () => {
    if (character.statusMessage) {
      return character.statusMessage;
    }
    const statusMap = {
      draft: 'Brouillon',
      pending: 'En attente',
      validated: 'Validé',
      rejected: 'Rejeté',
      abandoned: 'Abandonné',
      editing: 'En édition'
    };
    return statusMap[character.status] || character.status;
  };

  const shouldShowRoleRp = showRoleRp || roleRp !== undefined || !!roleRpEditor;
  const roleRpText = roleRp && roleRp.trim().length > 0 ? roleRp : roleRpPlaceholder;

  const handleOpenThemePage = () => {
    if (themePath) {
      navigate(themePath);
      setDropdownOpen(false);
    }
  };

  const isRow = variant === 'row';

  const titleNode = profilePath ? (
    <Link to={profilePath} className={styles.cardTitleLink}>
      {character.name}
    </Link>
  ) : (
    <h4 className={styles.cardTitle}>{character.name}</h4>
  );

  return (
    <div className={`${styles.card} ${isRow ? styles.cardRow : ''} ${dropdownOpen ? styles.cardDropdownOpen : ''}`}>
      {isRow ? (
        <div className={styles.rowAvatar}>
          {character.avatar ? (
            <img src={character.avatar} alt={character.name} className={styles.rowAvatarImage} />
          ) : (
            <div className={styles.rowAvatarPlaceholder} aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
          )}
        </div>
      ) : character.avatar && (
        <div className={styles.cardImageContainer}>
          <img
            src={character.avatar}
            alt={character.name}
            className={styles.cardImage}
          />
          <div className={styles.imageGradient}></div>
          <div className={styles.statusBadgeContainer}>
            <span className={`${styles.statusBadge} ${styles[character.status]}`}>
              {getStatusMessage()}
            </span>
          </div>
        </div>
      )}
      <div className={styles.cardBody}>
        <div className={styles.cardHeader}>
          {isRow ? (
            <>
              {titleNode}
              {((character.firstName || character.lastName) || character.status) && (
                <p className={styles.cardMeta}>
                  {(character.firstName || character.lastName) && (
                    <span>{[character.firstName, character.lastName].filter(Boolean).join(' ')}</span>
                  )}
                  {(character.firstName || character.lastName) && character.status && (
                    <span className={styles.metaSep} aria-hidden="true">·</span>
                  )}
                  {character.status && (
                    <span className={styles.statusText}>
                      <span className={`${styles.statusDot} ${styles[character.status]}`} aria-hidden="true" />
                      {getStatusMessage()}
                    </span>
                  )}
                </p>
              )}
              {character.alias && (
                <p className={styles.cardAlias}>{character.alias}</p>
              )}
            </>
          ) : (
            <>
              {titleNode}
              {(character.firstName || character.lastName) && (
                <p className={styles.cardSubtitle}>
                  {[character.firstName, character.lastName].filter(Boolean).join(' ')}
                </p>
              )}
            </>
          )}
        </div>
        
        <div className={styles.cardInfo}>
          {showUniverse && character.universe && (
            <div className={styles.infoItem}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span>{character.universe.name}</span>
            </div>
          )}
          {character.moralAffiliation && (
            <div className={styles.infoItem}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2L2 7l10 5 10-5-10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
              <span>{character.moralAffiliation}</span>
            </div>
          )}
          {shouldShowRoleRp && (
            <div
              className={`${styles.infoItem} ${onRoleRpClick && !roleRpEditor ? styles.infoItemInteractive : ''}`}
              onClick={() => {
                if (onRoleRpClick && !roleRpEditor) {
                  onRoleRpClick();
                }
              }}
              onKeyDown={(event) => {
                if (!onRoleRpClick || roleRpEditor) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onRoleRpClick();
                }
              }}
              role={onRoleRpClick && !roleRpEditor ? 'button' : undefined}
              tabIndex={onRoleRpClick && !roleRpEditor ? 0 : undefined}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 20h10" />
                <path d="M9 16h6" />
                <path d="M12 2 6 7v4c0 4 2.5 7.5 6 9 3.5-1.5 6-5 6-9V7z" />
              </svg>
              {roleRpEditor ? (
                <div className={styles.infoItemEditor}>{roleRpEditor}</div>
              ) : (
                <span>{roleRpText}</span>
              )}
            </div>
          )}
        </div>

        <div className={styles.cardActions}>
          {profilePath && (
            <Link
              to={profilePath}
              className={`${styles.btnIcon} ${styles.desktopOnly}`}
              title="Voir la fiche du personnage"
              aria-label="Voir la fiche du personnage"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </Link>
          )}
          {viewPath && (
            <button
              className={`${styles.btnIcon} ${styles.desktopOnly}`}
              onClick={handleView}
              title="Voir la fiche de présentation"
              aria-label="Voir la fiche de présentation"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          )}
          {editPath && (
            <button
              className={`${styles.btnIcon} ${styles.desktopOnly}`}
              onClick={handleEdit}
              title="Modifier"
              aria-label="Modifier le personnage"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {onEditAvatar && (
            <button
              className={`${styles.btnIcon} ${styles.desktopOnly}`}
              onClick={handleEditAvatar}
              title="Modifier l'avatar"
              aria-label="Modifier l'avatar"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </button>
          )}
          {themePath && (
            <button
              className={`${styles.btnIcon} ${styles.desktopOnly}`}
              onClick={handleOpenThemePage}
              title="Thème du personnage"
              aria-label="Thème du personnage"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <path d="M9 9h6v6H9z" />
              </svg>
            </button>
          )}
          {onAbandon && (
            <button
              className={`${styles.btnIcon} ${styles.btnIconDanger} ${styles.desktopOnly}`}
              onClick={handleAbandon}
              title="Abandonner"
              aria-label="Abandonner le personnage"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          )}
          {onDelete && (
            <button
              className={`${styles.btnIcon} ${styles.btnIconDanger} ${styles.desktopOnly}`}
              onClick={handleDelete}
              title="Supprimer"
              aria-label="Supprimer le personnage"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          )}
          {(profilePath || viewPath || editPath || onEditAvatar || themePath || onAbandon || onDelete) && (
          <div className={`${styles.dropdownContainer} ${styles.mobileOnly}`} ref={dropdownRef}>
            <button
              className={styles.btnIcon}
              onClick={() => setDropdownOpen(!dropdownOpen)}
              title="Actions"
              aria-label="Actions"
              aria-expanded={dropdownOpen}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="1" />
                <circle cx="19" cy="12" r="1" />
                <circle cx="5" cy="12" r="1" />
              </svg>
            </button>
            {dropdownOpen && (
              <div className={styles.dropdownMenu}>
                {profilePath && (
                  <Link
                    to={profilePath}
                    className={`${styles.dropdownItem} ${styles.mobileOnly}`}
                    onClick={() => setDropdownOpen(false)}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    <span>Voir la fiche</span>
                  </Link>
                )}
                {viewPath && (
                  <button
                    className={`${styles.dropdownItem} ${styles.mobileOnly}`}
                    onClick={handleView}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    <span>Fiche de présentation</span>
                  </button>
                )}
                {onEditAvatar && (
                  <button
                    className={`${styles.dropdownItem} ${styles.mobileOnly}`}
                    onClick={handleEditAvatar}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                    <span>Modifier l'avatar</span>
                  </button>
                )}
                {themePath && (
                  <button
                    className={`${styles.dropdownItem} ${styles.mobileOnly}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenThemePage();
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <path d="M9 9h6v6H9z" />
                    </svg>
                    <span>Thème du personnage</span>
                  </button>
                )}
                {editPath && (
                  <button
                    className={`${styles.dropdownItem} ${styles.mobileOnly}`}
                    onClick={handleEdit}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                    <span>Modifier</span>
                  </button>
                )}
                {onAbandon && (
                  <button
                    className={`${styles.dropdownItem} ${styles.dropdownItemDanger} ${styles.mobileOnly}`}
                    onClick={handleAbandon}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    <span>Abandonner</span>
                  </button>
                )}
                {onDelete && (
                  <button
                    className={`${styles.dropdownItem} ${styles.dropdownItemDanger} ${styles.mobileOnly}`}
                    onClick={handleDelete}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                    <span>Supprimer</span>
                  </button>
                )}
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CharacterCard;

